import { useState, useEffect, useCallback, useMemo } from "react";
import moment from "moment";
import { isSameDay, startOfMonth, endOfMonth } from "date-fns";
import { appointment_status } from "@/models/appointment.model";
import { AppointmentsRepository, AppointmentJoin } from "@/repositories/appointments.repository";
import { useAppSelector, useAppDispatch } from "@/lib/store";
import { organization_type } from "@/models/organization.model";
import { setCalendarSelectedDate } from "@/lib/config.slice";

export interface UseAppointmentCalendarOptions {
  maxAppointments?: number;
  initialDate?: Date;
  layer?: "pmc" | "vendor" | "subs" | "individual";
  currentMonth?: Date;
}

export const useAppointmentCalendar = (options: UseAppointmentCalendarOptions = {}) => {
  const { maxAppointments = 200, initialDate = moment().toDate(), layer, currentMonth } = options;

  const dispatch = useAppDispatch();
  const selectedDateString = useAppSelector((state) => state.config.calendarSelectedDate);
  const selectedDate = useMemo(() => moment(selectedDateString).toDate(), [selectedDateString]);

  // Custom setter that updates Redux state
  const setSelectedDate = useCallback(
    (date: Date) => {
      dispatch(setCalendarSelectedDate(date.toISOString()));
    },
    [dispatch]
  );

  const [appointments, setAppointments] = useState<AppointmentJoin[]>([]);
  const [allAppointments, setAllAppointments] = useState<AppointmentJoin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<appointment_status | "all">("all");

  const activeOrg = useAppSelector((state) => {
    const activeOrgId = state.organization.activeOrgId;
    return state.organization.organizations.find((org) => org.id === activeOrgId);
  });

  // Determine which API method to use based on organization type or explicit layer
  const getRepositoryMethod = useCallback(() => {
    const currentLayer =
      layer ||
      (activeOrg?.type === organization_type.subs
        ? "subs"
        : activeOrg?.type === organization_type.vendor
        ? "vendor"
        : "pmc");

    switch (currentLayer) {
      case "subs":
        return AppointmentsRepository.listForSubs;
      case "vendor":
        return AppointmentsRepository.listForVendor;
      case "individual":
        return AppointmentsRepository.listForIndividual;
      case "pmc":
      default:
        return AppointmentsRepository.list;
    }
  }, [layer, activeOrg?.type]);

  // Memoize the month key to only refetch when month/year changes
  const monthKey = useMemo(() => {
    const monthToUse = currentMonth || selectedDate;
    return moment(monthToUse).format("YYYY-MM");
  }, [currentMonth, selectedDate]);

  // Load appointments for the current month
  const loadAppointments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const repositoryMethod = getRepositoryMethod();
      if (!repositoryMethod) {
        throw new Error("No repository method available for current organization type");
      }

      // Calculate date range for fetching (expand to cover a wider range to avoid frequent refetches)
      const monthToUse = currentMonth || selectedDate;
      const monthStart = startOfMonth(monthToUse);
      const monthEnd = endOfMonth(monthToUse);

      // Fetch appointments with date range filter to only get relevant appointments
      const response = await repositoryMethod(1, maxAppointments, {
        dateFrom: moment(monthStart).format("YYYY-MM-DD"),
        dateTo: moment(monthEnd).format("YYYY-MM-DD"),
      });

      // Store all appointments - filtering for display happens client-side
      setAllAppointments(response.data);
    } catch (err) {
      console.error("Failed to load appointments:", err);
      setError(err instanceof Error ? err.message : "Failed to load appointments");
      setAppointments([]);
      setAllAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [getRepositoryMethod, maxAppointments, monthKey]);

  // Update appointments for selected day
  const updateDayAppointments = useCallback(
    (allAppts: AppointmentJoin[], date: Date) => {
      let dayAppointments = allAppts.filter((apt) => isSameDay(moment(apt.date).toDate(), date));

      // Apply status filter
      if (statusFilter !== "all") {
        dayAppointments = dayAppointments.filter((apt) => apt.status === statusFilter);
      }

      setAppointments(dayAppointments);
    },
    [statusFilter]
  );

  // Get appointment count for a date
  const getAppointmentCount = useCallback(
    (date: Date) => {
      let dayAppts = allAppointments.filter((apt) => isSameDay(moment(apt.date).toDate(), date));
      if (statusFilter !== "all") {
        dayAppts = dayAppts.filter((apt) => apt.status === statusFilter);
      }
      return dayAppts.length;
    },
    [allAppointments, statusFilter]
  );

  // Check if date has appointments
  const hasAppointments = useCallback(
    (date: Date) => {
      return getAppointmentCount(date) > 0;
    },
    [getAppointmentCount]
  );

  // Load appointments on mount and when active org or monthKey changes
  useEffect(() => {
    if (activeOrg) {
      loadAppointments();
    }
  }, [activeOrg, monthKey, loadAppointments]);

  // Update appointments when selected date or filter changes (no refetch, just filter existing data)
  useEffect(() => {
    if (!loading) {
      updateDayAppointments(allAppointments, selectedDate);
    }
  }, [updateDayAppointments, allAppointments, selectedDate, loading]);

  return {
    // State
    selectedDate,
    setSelectedDate,
    appointments,
    allAppointments,
    loading,
    error,
    statusFilter,
    setStatusFilter,

    // Computed values
    hasAppointments,
    getAppointmentCount,

    // Actions
    loadAppointments,
    updateDayAppointments,

    // Organization info
    activeOrg,
    currentLayer:
      layer ||
      (activeOrg?.type === organization_type.subs
        ? "subs"
        : activeOrg?.type === organization_type.vendor
        ? "vendor"
        : "pmc"),
  };
};
